import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Indicators,
} from 'ui'
import { toast } from 'react-hot-toast'
import { useCreateCityMutation } from '../queries'

// Validation schema
const schema = yup.object({
  city: yup.string().required('County name cannot be blank'),
})

export type NewCityFormValues = yup.InferType<typeof schema>

interface NewCityFormProps {
  onSuccess?: (data: any) => void
  onError?: (error: Error) => void
}

export const NewCityForm: React.FC<NewCityFormProps> = ({
  onSuccess,
  onError,
}) => {
  // Initialize the mutation
  const createCityMutation = useCreateCityMutation()

  // Initialize the form
  const form = useForm<NewCityFormValues>({
    defaultValues: {
      city: '',
    },
    resolver: yupResolver(schema),
    mode: 'onBlur',
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      // Show loading toast
      const toastId = toast.loading('Adding new county...')

      // Call the mutation
      const result = await createCityMutation.mutateAsync({
        city: data.city,
      })

      // Handle success
      toast.success('County added successfully', { id: toastId })
      form.reset()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(result)
      }
    } catch (error) {
      // Handle error
      toast.error('Failed to add county', {
        id: toast.loading(''), // Create and immediately dismiss a loading toast
      })

      // Call onError callback if provided
      if (onError && error instanceof Error) {
        onError(error)
      }
    }
  })

  return (
    <div className="flex flex-col w-full mb-6">
      <article className="mb-2">
        <h2 className="text-xl font-semibold">Add a New County</h2>
      </article>

      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>County Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter county name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center mb-auto mt-4">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="w-full sm:w-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner size="sm" className="mr-2" />
              ) : null}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
